import { memo, useCallback, useState } from 'react';

import { CheckIcon, ChevronDownIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface VariantSelectorItem {
  index: number;
  label: string;
}

export interface VariantSelectorProps {
  items: VariantSelectorItem[];
  activeIndex: number;
  onSelect: (index: number) => void;
  onDelete?: (index: number) => void;
  onAdd?: () => void;
  addButtonText?: string;
  hasError?: boolean;
  className?: string;
}

/**
 * Reusable variant selector dropdown for managing multiple indexed items
 * Supports selection, deletion (except index 0), and adding new items
 * Memoized to prevent unnecessary rerenders
 */
export const VariantSelector = memo(function VariantSelector({
  items,
  activeIndex,
  onSelect,
  onDelete,
  onAdd,
  addButtonText = 'Add variant',
  hasError = false,
  className = '',
}: VariantSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleSelect = useCallback(
    (index: number) => {
      onSelect(index);
      setOpen(false);
    },
    [onSelect]
  );

  const handleDelete = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (onDelete && index > 0) {
        onDelete(index);
        setOpen(false);
      }
    },
    [onDelete]
  );

  const handleAdd = useCallback(() => {
    if (onAdd) {
      onAdd();
      setOpen(false);
    }
  }, [onAdd]);

  const activeItem = items.find((item) => item.index === activeIndex);
  const selectedLabel = activeItem?.label || '';

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls="variant-selector-list"
          className={cn(
            'flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            hasError && 'focus-visible:ring-destructive/20',
            className
          )}
        >
          <span className="truncate overflow-hidden">{selectedLabel}</span>
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command shouldFilter={false}>
          <CommandList id="variant-selector-list">
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.index}
                  value={String(item.index)}
                  onSelect={() => handleSelect(item.index)}
                  onMouseEnter={() => setHoveredIndex(item.index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className={cn(
                    'flex items-center justify-between',
                    activeIndex === item.index && 'bg-selected-soft'
                  )}
                >
                  <span className="flex-1">{item.label}</span>
                  <div className="flex items-center gap-2">
                    {activeIndex === item.index && <CheckIcon className="h-4 w-4 text-primary" />}
                    {item.index > 0 && onDelete && hoveredIndex === item.index && (
                      <button
                        type="button"
                        onClick={(e) => handleDelete(item.index, e)}
                        className="group/delete ml-2"
                        aria-label={t('common.delete_with_type', { 0: item.label })}
                      >
                        {/* An explicit text color opts out of CommandItem's muted svg color. */}
                        <Trash2Icon className="h-4 w-4 text-muted-foreground group-hover/delete:text-destructive" />
                      </button>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            {onAdd && (
              <CommandGroup className="border-t pt-1">
                <CommandItem onSelect={handleAdd} className="flex items-center">
                  <PlusIcon className="mr-2 h-4 w-4" />
                  {addButtonText}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
